import { getCounterId } from './siteConfig';

export { getCounterId, getServerCounterId, subscribeCounterId } from './siteConfig';

type Metrica = {
  (...args: unknown[]): void;
  a?: unknown[][];
  l?: number;
};

declare global {
  interface Window {
    ym?: Metrica;
  }
}

export type AnalyticsGoal =
  | 'quote_request'
  | 'quote_start'
  | 'quote_success'
  | 'quote_error'
  | 'phone_click'
  | 'email_click'
  | 'map_open';

let enabledId: number | null = null;
let lastUrl: string | null = null;
let firstReferrer: string | null = null;
let landingUrl: string | null = null;

// Просмотры до прихода ID счётчика (site-config ещё грузится): без очереди переход внутри сайта
// в это время терял первую страницу. Отправляются по порядку сразу после init
const MAX_PENDING_PAGES = 20;
let pendingPages: { url: string; title: string }[] = [];

const send = (...args: unknown[]) => {
  try {
    window.ym?.(...args);
  } catch {
    /* Сбой аналитики не влияет на навигацию и отправку заявки. */
  }
};

/** Произвольные query и hash могут содержать поля формы; разрешены только метки кампаний. */
export const analyticsUrl = (value: string) => {
  const url = new URL(value, 'https://rpso.site');
  const knownPath =
    /^\/(?:projects|contacts|privacy|consent|cookies|404)?$/.test(url.pathname) ||
    /^\/catalog\/(?:smart-playground|sport-platforms|trainers)(?:\/[a-z]+-\d+)?$/.test(
      url.pathname,
    );
  const clean = new URL(knownPath ? url.pathname : '/404', url.origin);
  for (const key of [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'yclid',
    'gclid',
  ]) {
    const tag = url.searchParams.get(key);
    if (tag && tag.length <= 100 && /^[\w. ~-]+$/.test(tag)) clean.searchParams.set(key, tag);
  }
  return clean.href;
};

export const rememberReferrer = () => {
  landingUrl ??= analyticsUrl(window.location.href);
  if (firstReferrer !== null) return;
  try {
    const referrer = new URL(document.referrer);
    firstReferrer =
      referrer.origin === location.origin ? analyticsUrl(referrer.href) : referrer.origin;
  } catch {
    firstReferrer = '';
  }
};

export const setAnalyticsEnabled = (enabled: boolean) => {
  const id = getCounterId();
  // Отказ — очередь больше не нужна; нет ID — ждём конфиг, очередь держим
  if (!enabled) pendingPages = [];
  if (!enabled || !id) {
    if (enabledId) send(enabledId, 'destruct');
    enabledId = null;
    lastUrl = null;
    return;
  }
  if (enabledId === id) return;
  const ym: Metrica =
    window.ym ??
    ((...args: unknown[]) => {
      (ym.a ??= []).push(args);
    });
  window.ym = ym;
  ym.l ??= Date.now();
  enabledId = id;
  send(id, 'init', {
    url: analyticsUrl(window.location.href),
    referrer: firstReferrer ?? '',
    defer: true,
    clickmap: false,
    trackLinks: false,
    accurateTrackBounce: true,
    webvisor: true,
  });
  if (!document.getElementById('metrica-script')) {
    const script = document.createElement('script');
    script.id = 'metrica-script';
    script.async = true;
    script.src = 'https://mc.yandex.ru/metrika/tag.js';
    document.head.append(script);
  }
  const queued = pendingPages;
  pendingPages = [];
  for (const page of queued) trackPage(page.url, page.title);
};

export const trackPage = (url: string, title: string) => {
  const clean = analyticsUrl(url);
  if (!enabledId) {
    const last = pendingPages.at(-1);
    if (pendingPages.length < MAX_PENDING_PAGES && (!last || analyticsUrl(last.url) !== clean)) {
      pendingPages.push({ url, title });
    }
    return;
  }
  if (clean === lastUrl) return;
  const hitUrl = new URL(clean);
  if (lastUrl === null && landingUrl) {
    // Сохраняем источник, даже если разрешение дали после перехода внутри сайта.
    for (const [key, value] of new URL(landingUrl).searchParams) {
      if (!hitUrl.searchParams.has(key)) hitUrl.searchParams.set(key, value);
    }
  }
  send(enabledId, 'hit', hitUrl.href, { title, referer: lastUrl ?? firstReferrer ?? '' });
  lastUrl = clean;
};

/** В параметры целей никогда не попадают значения из формы. */
export const trackGoal = (goal: AnalyticsGoal) => {
  if (enabledId) send(enabledId, 'reachGoal', goal);
};
